import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "src/store";
import { filesize } from "src/ui/utils/filesize";

import { BuildArtifact } from "@dashboard/files/types";
import { updateFullOtaCompatibility } from "@dashboard/products/store";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { PrimaryButton } from "@shared/CustomButton";
import { TextField as FormTextField } from "@shared/form/TextField";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { useStyles } from "./OtaModal.style";

type Props = {
    otaFiles: BuildArtifact[];
};

type CompatibilityFormValues = {
    compatibility: string;
};

export const OtaFilesList: React.FC<Props> = (props: Props) => {
    const { t } = useTranslation(["common", "modals"]);
    const { classes } = useStyles();
    const dispatch = useDispatch();

    const handleSave = async (values: CompatibilityFormValues, fileId: number, form: FormApi<CompatibilityFormValues>) => {
        if (!fileId) {
            return;
        }

        const compatibilityValue = values.compatibility || "";

        try {
            await dispatch(
                updateFullOtaCompatibility({
                    fileId,
                    compatibility: compatibilityValue,
                }),
            ).unwrap();
            showSuccessToast(t("modals:buildSettings.toast.compatibilitySavedSuccess"));
            form.restart({ compatibility: compatibilityValue });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : t("modals:buildSettings.toast.compatibilitySavedError");
            showErrorToast(errorMessage);
        }
    };

    return (
        <Box>
            <Box className={classes.filesContainer}>
                {props.otaFiles.map((artifact, index) => {
                    const isIncrementalFile = artifact.name?.toLowerCase().includes("incremental") ?? false;
                    const displayName = artifact.name ?? "";

                    return (
                        <Card key={artifact.id || index} variant="outlined">
                            <CardContent>
                                <Box className={classes.cardContent}>
                                    <Box className={classes.metadataRow}>
                                        <Box className={classes.propertyItem}>
                                            <Typography variant="body2" color="textSecondary">
                                                {t("modals:buildSettings.filenameLabel")}
                                            </Typography>
                                            <Typography variant="body1" className={classes.propertyValue}>
                                                {displayName}
                                            </Typography>
                                        </Box>
                                        <Box className={classes.propertyItem}>
                                            <Typography variant="body2" color="textSecondary">
                                                {t("modals:buildSettings.fileSizeLabel")}
                                            </Typography>
                                            <Typography variant="body1" className={classes.propertyValue}>
                                                {filesize(artifact.size)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {artifact.id && (
                                        <>
                                            {isIncrementalFile ? (
                                                <Box className={classes.compatibilityContainer}>
                                                    <Box className={classes.propertyItem}>
                                                        <Typography variant="body2" color="textSecondary">
                                                            {t("modals:buildSettings.compatibilityLabel")}
                                                        </Typography>
                                                        <Typography variant="body1" className={classes.propertyValue}>
                                                            {artifact.compatibility || "-"}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Form<CompatibilityFormValues>
                                                    key={`${artifact.id}-${artifact.compatibility ?? ""}`}
                                                    initialValues={{ compatibility: artifact.compatibility || "" }}
                                                    onSubmit={(values, form) => handleSave(values, artifact.id!, form)}
                                                    render={({ handleSubmit, submitting, pristine }) => (
                                                        <Box
                                                            component="form"
                                                            onSubmit={handleSubmit}
                                                            className={classes.compatibilityContainer}
                                                        >
                                                            <FormTextField
                                                                name="compatibility"
                                                                label={t("modals:buildSettings.compatibilityLabel")}
                                                                fullWidth
                                                                className={classes.compatibilityInput}
                                                                disabled={submitting}
                                                            />
                                                            <PrimaryButton
                                                                type="submit"
                                                                className={classes.saveButton}
                                                                loading={submitting}
                                                                disabled={submitting || pristine}
                                                            >
                                                                {t("common:button.save")}
                                                            </PrimaryButton>
                                                        </Box>
                                                    )}
                                                />
                                            )}
                                        </>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>
        </Box>
    );
};
